import type { DocumentV2 as DocumentType } from '@eigtech/documents-types'
import { ensureJobCorn } from '@eigtech/restoration-util'
import { ROUTER_PATHS } from '@/lib/constants'
import { withRequiredPermissions } from '@/lib/permissions'
import { canEditDocument } from '@/lib/utils'
import { getUserCohort, useAuth0 } from '@eigtech/ui-shared-auth'
import { DataGrid } from '@eigtech/ui-shared-data-grid'
import {
  Box,
  Button,
  ButtonGroup,
  ButtonGroupProps,
  Center,
  ComposedAlert,
  ComposedCard,
  ConfirmModal,
  EmptyState,
  ExternalIcon,
  Flex,
  GridLoader,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  Toolbar,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@eigtech/ui-shared-dave'
import {
  getDocumentTitle,
  useDeleteDocument,
  useGetDocumentUrl,
  useGetEntityDocuments,
  useOrganizeDocuments,
} from '@eigtech/ui-shared-documents'
import {
  DocumentColumn,
  UpdateDocumentDrawer,
  documentColumnHelper,
  useSharedDocumentsColumns,
} from '@eigtech/ui-shared-documents-web'
import { LinkButton } from '@eigtech/ui-shared-router'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { MdEdit as EditIcon, MdDelete, MdPostAdd } from 'react-icons/md'
import { useMount } from 'react-use'

export const Route = createFileRoute('/_auth/jobs/$jobId/_layout/documents')({
  component: () => <JobDetailDocuments />,
})

const JobDetailDocuments = withRequiredPermissions(BaseJobDetailDocuments, {
  requiredPermissions: { action: 'read', subject: 'job' },
})

function BaseJobDetailDocuments() {
  const { jobId } = Route.useParams()

  const { user } = useAuth0()

  // delegate component, default to insured cohort
  const cohort = getUserCohort(user) ?? 'technician'

  const {
    data: documents,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useGetEntityDocuments({
    cohort,
    entityId: ensureJobCorn(jobId),
  })

  useMount(() => {
    if (!jobId) return
    refetch()
  })

  const columns = useDocumentsColumns()

  const groupedDocuments = useOrganizeDocuments(documents)

  const orientation = useBreakpointValue<ButtonGroupProps['orientation']>({
    base: 'vertical',
    md: 'horizontal',
  })

  return (
    <>
      <Stack spacing="6">
        <Stack>
          <Toolbar>
            <Stack
              alignItems={{ base: 'flex-start', md: 'center' }}
              direction={{ base: 'column', md: 'row' }}
              justifyContent="space-between"
            >
              <Heading as="h3" size="md">
                Documents
              </Heading>

              <ButtonGroup
                orientation={orientation}
                spacing="2"
                variant="outline"
                width={{ base: '100%', md: 'auto' }}
              >
                <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailPhotoReport.route}>
                  Create Photo Report
                </LinkButton>
                <LinkButton
                  params={{ jobId }}
                  to={ROUTER_PATHS.jobDetailUploadDocuments.route}
                  variant="solid"
                >
                  Upload Document(s)
                </LinkButton>
              </ButtonGroup>
            </Stack>
          </Toolbar>
        </Stack>

        <ComposedCard display={{ base: 'none', lg: 'block' }}>
          {!isPending && !isError && !documents.length ? (
            <EmptyState heading="Get started with uploading some documents" icon={MdPostAdd}>
              <Stack spacing="4">
                <Text>
                  If you have any documents you need to upload for your job, you can do so by
                  clicking the button below.
                </Text>
                <Center>
                  <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailUploadDocuments.route}>
                    Upload Document(s)
                  </LinkButton>
                </Center>
              </Stack>
            </EmptyState>
          ) : (
            <DataGrid<DocumentColumn>
              columns={columns}
              data={groupedDocuments}
              enableColumnFilters={false}
              enableColumnResizing={false}
              enableGlobalFilter={false}
              enableHiding={false}
              getSubRows={(row) => row.subRows}
              isError={isError}
              isFetching={isFetching}
              isPending={isPending}
              refetch={refetch}
            />
          )}
        </ComposedCard>

        <Box display={{ base: 'block', lg: 'none' }}>
          {isPending ? (
            <GridLoader
              columns={{ base: 1, md: 2 }}
              numberOfSkeletonLoaders={4}
              skeletonProps={{ h: 320 }}
            />
          ) : isError ? (
            <ComposedAlert
              alert={{
                title: 'Something went wrong',
                description:
                  'We could not load the documents for this job. Please try refreshing. If the issue persists, please contact us for support.',
              }}
              status="error"
            />
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
              {groupedDocuments.map((doc) => (
                <ComposedCard
                  key={doc.id}
                  heading={
                    <Heading as="h3" size="sm">
                      {getDocumentTitle(doc)}
                    </Heading>
                  }
                  w="full"
                >
                  <Stack>
                    <Box>
                      <Text textStyle="overline">Filename</Text>
                      <Text wordBreak="break-word">{doc.filename || '(None)'}</Text>
                    </Box>

                    <Box>
                      <Text textStyle="overline">Description</Text>
                      <Text wordBreak="break-word">{doc.description || '(None)'}</Text>
                    </Box>

                    <DocumentActions {...doc} />
                  </Stack>
                </ComposedCard>
              ))}
            </SimpleGrid>
          )}
        </Box>
      </Stack>
    </>
  )
}

function useDocumentsColumns() {
  const sharedColumns = useSharedDocumentsColumns()

  return useMemo(
    () => [
      sharedColumns.title,
      sharedColumns.description,
      sharedColumns.filename,
      sharedColumns.createdDate,
      documentColumnHelper.display({
        id: 'download',
        header: '',
        maxSize: 80,
        enableSorting: false,
        cell: (cell) => <DocumentActions {...cell.row.original} />,
      }),
    ],
    [sharedColumns]
  )
}

function DocumentActions(document: DocumentType) {
  const {
    data: url,
    isPending,
    isError,
    refetch,
  } = useGetDocumentUrl({ entityId: document.entityId, documentId: document.id })

  useMount(() => {
    refetch()
  })

  const [isOpenUpdateDocumentDrawer, setIsOpenUpdateDocumentDrawer] = useState(false)

  const { mutateAsync: deleteDocument, isPending: isDeleting } = useDeleteDocument()

  const toast = useToast()
  const {
    isOpen: isConfirmModalOpen,
    onOpen: onOpenConfirmModal,
    onClose: onCloseConfirmModal,
  } = useDisclosure()

  async function confirmDeleteDocument() {
    await deleteDocument({ entityId: document.entityId, documentId: document.id })

    toast({
      status: 'success',
      title: 'Successfully deleted document',
    })
  }

  const { user } = useAuth0()
  const cohort = getUserCohort(user) ?? 'technician'
  const canEdit = canEditDocument(document.source, cohort)

  return (
    <>
      {canEdit && (
        <Flex display={{ base: 'none', lg: 'flex' }} justifyContent="flex-end">
          <ButtonGroup variant="outline">
            <Button onClick={() => setIsOpenUpdateDocumentDrawer(true)}>Update File Info</Button>

            <Button isLoading={isDeleting} rightIcon={<MdDelete />} onClick={onOpenConfirmModal}>
              Delete
            </Button>
          </ButtonGroup>
        </Flex>
      )}

      <Stack display={{ base: undefined, lg: 'none' }}>
        <ButtonGroup mb="2" variant="outline" w="full">
          {canEdit && (
            <Button
              rightIcon={<EditIcon />}
              w="full"
              onClick={() => setIsOpenUpdateDocumentDrawer(true)}
            >
              Edit Info
            </Button>
          )}

          <Button
            as="a"
            href={url}
            isDisabled={isError}
            isLoading={isPending}
            rightIcon={<ExternalIcon />}
            target={document.id}
            w="full"
          >
            Open
          </Button>
        </ButtonGroup>

        {canEdit && (
          <Button
            colorScheme="red"
            isLoading={isDeleting}
            rightIcon={<MdDelete />}
            variant="outline"
            w="full"
            onClick={onOpenConfirmModal}
          >
            Delete
          </Button>
        )}
      </Stack>

      {canEdit && isConfirmModalOpen && (
        <ConfirmModal
          confirmLabel="Delete"
          confirmProps={{ colorScheme: 'red' }}
          isOpen
          isPending={isDeleting}
          title="Delete Document"
          onClose={onCloseConfirmModal}
          onConfirm={confirmDeleteDocument}
        >
          Are you sure you want to delete this document?
        </ConfirmModal>
      )}

      {canEdit && isOpenUpdateDocumentDrawer && (
        <UpdateDocumentDrawer
          document={document}
          open={isOpenUpdateDocumentDrawer}
          onOpenChange={setIsOpenUpdateDocumentDrawer}
        />
      )}
    </>
  )
}
